import React, { useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, Row, Spinner } from "react-bootstrap";
import { getOrders } from "../../services/OrderService";
import { Order } from "../../types";

interface OverviewCard {
  icon: string;
  label: string;
  tone: string;
  accent: string;
  chip: string;
  count: number;
}

const Dashboard: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getOrders();
        setOrders(response || []);
      } catch {
        setError("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const overviewCards = useMemo<OverviewCard[]>(() => {
    const pendingOrders = orders
      .filter((order) => {
        const status = (order.status || "").toLowerCase();
        return status.includes("pending") || status.includes("progress");
      }).length;

    const urgentOrders = orders.filter((order) => {
      if (!order.dateRequired) return false;

      const requiredDate = new Date(order.dateRequired);
      if (Number.isNaN(requiredDate.getTime())) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      requiredDate.setHours(0, 0, 0, 0);

      const diffInDays = Math.ceil(
        (requiredDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      );
      const status = (order.status || "").toLowerCase();

      return diffInDays <= 2 && !status.includes("complete");
    }).length;

    return [
      {
        icon: "bi-basket2-fill",
        label: "Total Orders",
        tone: "bg-warning-subtle",
        accent: "text-warning-emphasis",
        chip: "Lifetime",
        count: orders.length,
      },
      {
        icon: "bi-clock-fill",
        label: "Pending Orders",
        tone: "bg-info-subtle",
        accent: "text-info-emphasis",
        chip: "In Queue",
        count: pendingOrders,
      },
      {
        icon: "bi-exclamation-lg",
        label: "Urgent Orders",
        tone: "bg-danger-subtle",
        accent: "text-danger-emphasis",
        chip: "Critical",
        count: urgentOrders,
      },
    ];
  }, [orders]);

  return (
    <div className="container-xl py-4">
      <div className="mb-4">
        <p className="mb-2 small fw-semibold text-uppercase text-secondary">
          Daily Overview
        </p>
        <h1 className="h2 mb-0 fw-semibold text-dark">Dashboard</h1>
      </div>

      {error && (
        <Alert variant="warning" className="mb-4">
          {error}
        </Alert>
      )}

      <Row className="g-3 g-lg-4 justify-content-center">
        {overviewCards.map((card) => (
          <Col key={card.label} xs={12} sm={10} md={8} lg={6} xl={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-3 p-lg-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div
                    className={`rounded-4 p-2 p-lg-3 d-inline-flex align-items-center justify-content-center ${card.tone} ${card.accent}`}
                  >
                    <i className={`bi ${card.icon} fs-4`}></i>
                  </div>
                  <span
                    className={`badge rounded-pill text-uppercase fw-semibold px-3 py-2 ${card.tone} ${card.accent}`}
                  >
                    {card.chip}
                  </span>
                </div>

                <div className="text-body fs-5 mb-2">{card.label}</div>
                <div className="display-5 fw-semibold lh-1">
                  {loading ? (
                    <Spinner animation="border" />
                  ) : (
                    card.count.toLocaleString()
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default Dashboard;

